import api from './api';
import type { AdminUser, AdminRole } from './authService';

/**
 * GET /admin/me — fetch the wine-api's view of the currently-authenticated
 * user. Used by the Nota-login flow to hydrate `role` + `assignedVenueIds`
 * (which are local AdminUser concepts, not Nota concepts).
 *
 * Auth header is attached automatically by the api.ts request interceptor,
 * which reads `localStorage.access_token`. Callers must therefore persist
 * the token BEFORE invoking this — the Nota login flow does that via
 * `notaAuthService.persistTokens` before calling here.
 */

interface MeResponse {
  user: {
    id: string;
    email: string;
    role: AdminRole;
    assignedVenueIds: string[];
    active: boolean;
  };
}

/**
 * Returns a fully-populated AdminUser shape suitable for `localStorage.user`
 * and `AuthContext.user`. Throws on network / auth failure — callers should
 * treat any throw as "session is unusable, fall back to login screen".
 */
export const fetchCurrentUser = async (): Promise<AdminUser> => {
  const response = await api.get<MeResponse>('/admin/me');
  const u = response.data.user;
  return {
    id: u.id,
    _id: u.id, // mirror AdminUser dual-id convention
    email: u.email,
    firstName: '',
    lastName: '',
    role: u.role,
    assignedVenueIds: u.assignedVenueIds ?? [],
    passwordChangeRequired: false, // Nota owns password lifecycle
    active: u.active,
    createdAt: '',
    updatedAt: '',
  };
};
